import React, { useEffect, useState, useCallback } from 'react'
import { Bot, RotateCcw, Save } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import AdminSidebar from '@/features/admin/components/AdminSidebar'
import * as adminApi from '@/features/admin/services/adminApi'

const MAX_CHARS = 8000

const AdminAIConfigPage: React.FC = () => {
  const { currentTheme } = useTheme()

  const [value, setValue] = useState('')
  const [savedValue, setSavedValue] = useState('')
  const [defaultValue, setDefaultValue] = useState('')
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const res = await adminApi.getSystemPrompt()
        if (cancelled) return
        setValue(res.value)
        setSavedValue(res.value)
        setDefaultValue(res.defaultValue)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [])

  const isDirty = value !== savedValue
  const charCount = value.length

  const handleSave = useCallback(async () => {
    if (!isDirty || saving) return
    setSaving(true)
    setError(null)
    setSuccessMsg(null)

    try {
      const result = await adminApi.updateSystemPrompt(value)
      setSavedValue(result.value)
      setValue(result.value)
      setLastUpdatedAt(result.updatedAt)
      setSuccessMsg('Saved. Changes take effect on the next message.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }, [isDirty, saving, value])

  const handleReset = useCallback(() => {
    if (defaultValue) {
      setValue(defaultValue)
      setSuccessMsg(null)
    }
  }, [defaultValue])

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    } catch {
      return iso
    }
  }

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: currentTheme.primary }}
    >
      <AdminSidebar />

      <main className="lg:pl-56 min-h-screen">
        <div className="px-4 pt-16 pb-8 sm:px-6 lg:px-8 lg:pt-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold" style={{ color: currentTheme.text }}>
              AI Configuration
            </h1>
            <p className="mt-1 text-sm" style={{ color: currentTheme.secondrytext }}>
              Manage Zeph's persona and behaviour instructions
            </p>
          </div>

          <div
            className="rounded-2xl border p-6"
            style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}
          >
            {/* Card header */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="flex items-center justify-center w-9 h-9 rounded-xl"
                style={{ backgroundColor: `${currentTheme.accent}20` }}
              >
                <Bot size={18} style={{ color: currentTheme.accent }} />
              </div>
              <div>
                <h2 className="text-base font-semibold" style={{ color: currentTheme.text }}>
                  System Prompt
                </h2>
                <p className="text-xs" style={{ color: currentTheme.secondrytext }}>
                  Instructions injected at the start of every conversation, on every channel
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <span className="text-sm" style={{ color: currentTheme.secondrytext }}>Loading…</span>
              </div>
            ) : (
              <>
                <textarea
                  value={value}
                  onChange={(e) => { setValue(e.target.value); setSuccessMsg(null) }}
                  maxLength={MAX_CHARS}
                  rows={18}
                  className="w-full rounded-xl border text-sm font-mono resize-y p-4 focus:outline-none transition-colors"
                  style={{
                    backgroundColor: currentTheme.primary,
                    color: currentTheme.text,
                    borderColor: currentTheme.border,
                  }}
                  placeholder="Enter the system prompt…"
                />

                {/* Char count + last saved */}
                <div className="flex items-center justify-between mt-2 mb-5">
                  <span
                    className="text-xs"
                    style={{ color: charCount > MAX_CHARS * 0.9 ? currentTheme.errorText : currentTheme.secondrytext }}
                  >
                    {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                  </span>
                  {lastUpdatedAt && (
                    <span className="text-xs" style={{ color: currentTheme.secondrytext }}>
                      Last saved {formatDate(lastUpdatedAt)}
                    </span>
                  )}
                </div>

                {error && (
                  <p className="mb-4 text-sm text-red-500">{error}</p>
                )}
                {successMsg && (
                  <p className="mb-4 text-sm" style={{ color: currentTheme.successText }}>{successMsg}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={!isDirty || saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: currentTheme.accent, color: '#fff' }}
                  >
                    <Save size={15} />
                    {saving ? 'Saving…' : 'Save'}
                  </button>

                  {defaultValue && value !== defaultValue && (
                    <button
                      type="button"
                      onClick={handleReset}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
                      style={{
                        backgroundColor: currentTheme.primary,
                        color: currentTheme.secondrytext,
                        border: `1px solid ${currentTheme.border}`,
                      }}
                    >
                      <RotateCcw size={15} />
                      Reset to default
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default AdminAIConfigPage
